import { Upload, message } from 'antd';
import ImgCrop from 'antd-img-crop';
import axios, { AxiosProgressEvent } from 'axios';
import React, { useRef } from 'react';

import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { BlobIndexTag, BlobMetadataField } from '../../../generated';
import { FileValidator } from './file-validator';
import { sanitizeFilename, asciiNormalizeFilename } from '../../../utils/filename';


export interface AzureBlobResponse {
  versionId: string;
  blobUrl: string;
}

export interface AuthResult {
  isAuthorized: boolean;
  authHeader: {
    authHeader?: string;
    requestDate?: string;
    indexTags?: BlobIndexTag[];
    metadataFields?: BlobMetadataField[];
    blobPath?: string;
    blobName?: string;
  };
}
interface AzureUploadProps {
  data: {
    permittedContentTypes?: string[];
    permittedExtensions?: string[];
    maxFileSizeBytes?: number;
    maxWidthOrHeight?: number;
    notificationCount?: number;
  };
  onInvalidContentType: () => void;
  onInvalidContentLength: () => void;
  onSuccess: (response: AzureBlobResponse, file: UploadFile) => Promise<void>;
  // Include a context object so callers can distinguish malicious (malware) errors.
  onError?: (file: File, error: unknown) => void;
  onRemoveRequested?: (file: UploadFile<unknown>) => Promise<boolean>;
  authorizeRequest: (file: File) => Promise<AuthResult>;
  cropperProps?: object;
  uploadProps?: UploadProps;
  children?: React.ReactNode;
  maxFileCount?: number;
  buttonText?: string;
  uploadPreviewImage?: boolean;
  // Provided by container: executes backend malware scan mutation (pollUrl derived internally)
  runMalwareScan: (blobUrl: string, versionId: string) => Promise<{ isSafe: boolean; isMalicious: boolean; scanResult?: string }>;
}

export const AzureUpload: React.FC<AzureUploadProps> = ({ uploadPreviewImage: uploadImage = true, ...props}) => {
  const authResultRef = useRef<AuthResult | undefined>(undefined);
  // runMalwareScan provided via props from container; backend handles malicious deletions.
 

  function renameFile(originalFile: RcFile, newName: string) {
    try {
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified
      });
    } catch (error) {
      console.error('Error occurred while renaming file:', error);
      return originalFile;
    }
  }

  const beforeUpload = async (file: RcFile) => {
    let sanitizedFilename = sanitizeFilename(file.name);
    sanitizedFilename = asciiNormalizeFilename(sanitizedFilename);
    sanitizedFilename = sanitizedFilename.replace(/[\s–—]+/g, '-');
    const sanitizedFile: RcFile = renameFile(file, sanitizedFilename) as RcFile;
    sanitizedFile.uid = file.uid;

    const validatorOptions = {
      maxFileSizeBytes: props.data.maxFileSizeBytes,
      maxWidthOrHeight: props.data.maxWidthOrHeight,
      permittedContentTypes: props.data.permittedContentTypes
    };

    const validator = new FileValidator(sanitizedFile, validatorOptions);
    const result = await validator.validate();

    if (!result.success) {
      console.error(`Error: ${result.message}`);

      switch (result.code) {
        case 'content-type':
          props.onInvalidContentType();
          break;
        case 'content-length':
          props.onInvalidContentLength();
          break;
        case 'default':
          console.log(`File did not upload, error: ${JSON.stringify(result.message)}`);
          props.onError?.(sanitizedFile, result.message);
          break;
      }

      return Upload.LIST_IGNORE;
    }

    const newFile = result.file;

    if (newFile) {
  const auth = await props.authorizeRequest(newFile);
  if (auth.isAuthorized) {
        //@ts-expect-error Legacy code pattern - dynamic property assignment on file object
  newFile['url'] = auth.blobPath;
  authResultRef.current = auth;
        return newFile;
      } else {
  props.onError?.(newFile, 'Error authorizing request');
      }
    }
    return Upload.LIST_IGNORE;
  };

  const getMetadataHeaders = (metadataFields?: BlobMetadataField[]) => {
    const metadataHeaders: Record<string, string> = {};
    if (metadataFields) {
      for (const value of Object.values(metadataFields)) {
        metadataHeaders['x-ms-meta-' + value.name] = value.value;
      }
    }
    return metadataHeaders;
  };

  const getIndexTagsHeaders = (indexTags?: BlobIndexTag[]) => {
    const indexTagsHeaders: Record<string, string> = {};
    if (indexTags) {
      const output = indexTags.reduce((acc: Record<string, string>, cur: { name: string; value: string }) => {
        acc[cur.name] = cur.value;
        return acc;
      }, {});

      indexTagsHeaders['x-ms-tags'] = new URLSearchParams(output).toString();
    }
    return indexTagsHeaders;
  };

  // eslint-disable-next-line complexity
  const customizeUpload =  async (option: any) => {
    if (authResultRef?.current?.isAuthorized) {

    const { authHeader } = authResultRef.current;
  try {
        option.file.url = authHeader.blobPath;

        const metadataHeaders = getMetadataHeaders(authHeader.metadataFields);
        const indexTagsHeaders = getIndexTagsHeaders(authHeader.indexTags);

        const isValidProgressEvent = (progressEvent: AxiosProgressEvent) => {
          return progressEvent?.total !== undefined && progressEvent.loaded !== undefined;
        }
        const headers = {
          ...option.headers,
          ...metadataHeaders,
          ...indexTagsHeaders,
          Authorization: authHeader.authHeader,
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-version': '2021-04-10',
          'x-ms-date': authHeader.requestDate,
          'Content-Type': option.file.type
        };
        const response = await axios.request({
          method: 'put',
          url: authHeader.blobPath,
          data: new Blob([option.file], { type: option.file.type }),
          headers: headers,
          onUploadProgress: (progressEvent) => {
            if (isValidProgressEvent(progressEvent) && progressEvent.total !== undefined) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              option.onProgress({ percent: percentCompleted }, option.file);
            }
          },
        });
        const blobResponse: AzureBlobResponse = {
          versionId: response.headers['x-ms-version-id'],
          blobUrl: response.request.responseURL
        };
        try {
          let malwareResult: { isSafe: boolean; isMalicious: boolean; scanResult?: string } | null = null;
          try {
            if (props.runMalwareScan) {
              malwareResult = await props.runMalwareScan(
                blobResponse.blobUrl,
                blobResponse.versionId
              );
            }
          } catch (mErr) {
            console.warn('[AzureUpload] malware scan invocation failed', mErr);
          }
          if (malwareResult?.isSafe) {
            await props.onSuccess(blobResponse, option.file as unknown as UploadFile);
          } else if (malwareResult?.isMalicious) {
            message.error('File upload rejected due to malware detection.');
              // Backend mutation already performed any necessary deletion of the malicious version.
            props.onError?.(option.file, 'File flagged as malicious');
            option.onError(new Error('File flagged as malicious'), option.file);
          } else {
            message.warning('Malware scan result unavailable (pending).');
            props.onError?.(option.file, 'Malware scan result unavailable');
            option.onError(new Error('Malware scan result unavailable'), option.file);
          }
        } catch (scanError) {
          console.error('[AzureUpload] Error during malware scan processing', scanError);
          message.error('Error verifying file safety.');
          props.onError?.(option.file as File, scanError);
          option.onError(scanError as any, option.file);
        }
      } catch (uploadError) {
  if (props.onError) props.onError(option.file, uploadError);
        option.onError(uploadError, option.file);
      }
    } else {
          //not authorized - do nothing
      }
      return option;
  };

  const validFileExtensions = props.data.permittedExtensions ? '.' + props.data.permittedExtensions.join(', .') : undefined;

  if (props.cropperProps) {
    return (
      <ImgCrop {...props.cropperProps}>
        <Upload
          accept={validFileExtensions}
          customRequest={customizeUpload}
          beforeUpload={beforeUpload}
          onRemove={(file) => {
            return props.onRemoveRequested ? props.onRemoveRequested(file) : undefined;
          }}
          showUploadList={false}
          progress={{
            strokeColor: {
              '0%': '#108ee9',
              '100%': '#87d068'
            },
            strokeWidth: 3,
            format: (percent) => (percent ? `${parseFloat(percent.toFixed(2))}%` : '')
          }}
          {...props.uploadProps}
        >
          {props.children}
        </Upload>
      </ImgCrop>
    );
  } else {
    return (
    <Upload
      accept={validFileExtensions}
      customRequest={customizeUpload}
      beforeUpload={beforeUpload}
      onRemove={(file) => {
        return props.onRemoveRequested ? props.onRemoveRequested(file) : undefined;
      }}
      maxCount={props.maxFileCount}
      showUploadList={false}
      progress={{
        strokeColor: {
          '0%': '#108ee9',
          '100%': '#87d068'
        },
        strokeWidth: 3,
        format: (percent) => (percent ? `${parseFloat(percent.toFixed(2))}%` : '')
      }}
      {...props.uploadProps}
    >
      {props.children}
    </Upload>
    );
  }
};
